/**
 * Distributor top bar page names -- the ONE source for the "<Page>" half of
 * "<Distributor> > <Page>" (Jake, 2026-10-19).
 *
 * Keyed on route NAME (names survive the path edits and withPrefix() rewrites
 * this router does constantly; same convention as helpContextMap.js).
 *
 * Sidebar and top bar share one vocabulary: DistributorLayout.vue imports
 * NAV_LABELS for every sidebar row, and the route table below reuses the same
 * constants, so a sidebar label and the title of the page it opens cannot
 * drift. `distributor-layout-topbar-title.spec.js` asserts this against the
 * rendered sidebar.
 *
 * ADDING A /distributor ROUTE: add its name here. The router audit
 * (`__tests__/distributor-topbar-titles.spec.js`) fails if a renderable
 * route has no title. A route may instead carry `meta.topbarTitle`, which
 * wins over this table.
 */

// Sidebar row labels (also the titles of the pages they open).
export const NAV_LABELS = Object.freeze({
    dashboard: "Dashboard",
    stores: "Stores",
    decorationLibrary: "Decoration Library",
    approvals: "Approvals",
    clients: "Clients",
    orders: "Orders",
    performance: "Performance",
    reports: "Reports",
    payouts: "Payouts",
    company: "Company",
    suppliers: "Suppliers",
    team: "Team & Permissions",
    payments: "Payments",
    storeDefaults: "Store defaults",
    compliance: "Compliance",
    notifications: "Notifications",
});

// Shown only when a route has no title at all (the audit spec keeps this from
// happening). The section name, never a wrong specific page like "Stores".
export const DISTRIBUTOR_SECTION_FALLBACK = "Distributor";

const L = NAV_LABELS;

export const TITLE_BY_ROUTE_NAME = Object.freeze({
    distributor_landing: L.dashboard,

    // Work
    distributor_stores: L.stores,
    store_add: "New Store",
    store_edit: "Edit Store",
    store_decoration_design_detail: "Design",
    decoration_library_list: L.decorationLibrary,
    decoration_library_detail: "Decoration",
    decoration_library_edit: "Edit Decoration",
    decoration_library_add: "New Decoration",
    library_artwork_detail: "Artwork",
    library_artwork_colorways: "Colorways",
    library_artwork_design_detail: "Design",
    decoration_sets_list: "Decoration Sets",
    decoration_sets_detail: "Decoration Set",
    approval_workspace: L.approvals,
    approval_workspace_detail: "Approval",
    client_list: L.clients,
    client_detail: "Client",

    // Catalog / product editing
    product_list: "Products",
    product_view: "Product",
    product_add: "New Product",
    product_edit: "Edit Product",
    product_editor: "Product Editor",
    product_editor_placement: "Placement Editor",

    // Reporting
    distributor_reporting_orders: L.orders,
    distributor_reporting_order_detail: "Order",
    distributor_reporting_order_new: "Add New Order",
    distributor_reporting_performance: L.performance,
    distributor_reporting_reports: L.reports,
    distributor_reporting_payouts: L.payouts,
    report: L.reports,

    // Account
    distributor_account_company: L.company,
    distributor_account_suppliers: L.suppliers,
    distributor_account_team: L.team,
    distributor_account_team_invite: "Invite Teammate",
    distributor_account_payments: L.payments,
    distributor_account_defaults: L.storeDefaults,
    distributor_account_compliance: L.compliance,
    distributor_personal_profile: "My Profile",

    // Help
    owner_help_index: "Help",
    owner_help_article: "Help",
});

/**
 * Page name for a resolved route (`$route`, or a `router.resolve()` result).
 * meta.topbarTitle > name table > section fallback.
 */
export function resolveDistributorPageTitle(route) {
    const fromMeta = route?.meta?.topbarTitle;
    if (fromMeta) return fromMeta;
    const fromName = route?.name ? TITLE_BY_ROUTE_NAME[route.name] : null;
    return fromName || DISTRIBUTOR_SECTION_FALLBACK;
}
